import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { cameraAttendanceApi } from './cameraAttendanceApi';

function todayYYYYMMDD() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeOverstayToPeople({ attendance = [], visitors = [], dailyWage = [] }) {
  const people = [];

  // Attendance overstay
  (Array.isArray(attendance) ? attendance : []).forEach((row, idx) => {
    const a_id = row?.a_id ?? row?.attendance_id ?? row?.id ?? null;
    people.push({
      id: `att-${a_id ?? idx}`,
      type: 'employee',
      backendType: 'attendance',
      recordId: a_id,
      name: row?.person_name ?? row?.employee_name ?? row?.name ?? 'Employee',
      role: row?.role ?? row?.designation ?? '',
      employeeId: row?.employee_id ?? row?.person_id ?? row?.emp_id ?? '',
      confidence: row?.confidence != null ? String(row.confidence) : '',
      shift: row?.shift ?? row?.shift_name ?? '',
      inTime: row?.in_time ?? row?.inTime ?? '',
      outTime: row?.out_time ?? row?.outTime ?? '',
      photo: row?.photo_url ?? row?.image_url ?? row?.image_presigned_url ?? row?.photo ?? '',
      raw: row,
    });
  });

  // Visitor overstay
  (Array.isArray(visitors) ? visitors : []).forEach((row, idx) => {
    const entry_id = row?.entry_id ?? row?.id ?? null;
    people.push({
      id: `vis-${entry_id ?? idx}`,
      type: 'visitor',
      backendType: 'visitor',
      recordId: entry_id,
      name: row?.visitor_name ?? row?.name ?? 'Visitor',
      role: row?.purpose ?? row?.role ?? 'Visitor',
      employeeId: row?.visitor_id ?? row?.mobile_number ?? row?.phone_number ?? '',
      confidence: row?.confidence != null ? String(row.confidence) : '',
      shift: row?.visit_slot ?? row?.slot ?? '',
      inTime: row?.in_time ?? row?.inTime ?? '',
      outTime: row?.out_time ?? row?.outTime ?? '',
      photo: row?.photo_url ?? row?.image_url ?? row?.photo ?? '',
      raw: row,
    });
  });

  // Daily wage overstay
  (Array.isArray(dailyWage) ? dailyWage : []).forEach((row, idx) => {
    const entry_id = row?.entry_id ?? row?.id ?? null;
    people.push({
      id: `dw-${entry_id ?? idx}`,
      type: 'employee',
      backendType: 'daily_wage',
      recordId: entry_id,
      name: row?.worker_name ?? row?.name ?? 'Daily wage',
      role: row?.role ?? 'Daily wage',
      employeeId: row?.worker_id ?? row?.person_id ?? '',
      confidence: row?.confidence != null ? String(row.confidence) : '',
      shift: row?.shift ?? '',
      inTime: row?.in_time ?? row?.inTime ?? '',
      outTime: row?.out_time ?? row?.outTime ?? '',
      photo: row?.photo_url ?? row?.image_url ?? row?.photo ?? '',
      raw: row,
    });
  });

  return people;
}

const SAFETY_CHECK = [
  { label: 'Helmet', value: 'Detected', ok: true },
  { label: 'Safety Vest', value: 'Detected', ok: true },
  { label: 'Gloves', value: 'Missing', ok: false },
  { label: 'Zone Status', value: 'PPE VIOLATION ALERT', ok: false },
];

const TODAY_LOGS = [
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:52 ENTRY Pooja Singh',
  '10:05 EXIT Ramesh Kumar',
  '10:12 ENTRY Anil Verma',
  '10:22 ENTRY Kiran Das',
  '11:03 ENTRY Visitor',
  '11:26 EXIT Pooja Singh',
  '12:08 ENTRY Ramesh Kumar',
  '12:40 EXIT Anil Verma',
  '13:15 ENTRY Pooja Singh',
  '14:02 EXIT Kiran Das',
  '15:05 EXIT Ramesh Kumar',
];

const LOG_PREVIEW_COUNT = 5;

const CameraAttendanceOverstayScreen = () => {
  const location = useLocation();
  const [people, setPeople] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { openOverlay } = useOverlay();
  const navigate = useNavigate();
  const [siteId, setSiteId] = useState('');
  const [date, setDate] = useState(todayYYYYMMDD());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const fromQuerySite = params.get('site_id') || params.get('siteId') || '';
    const fromStateSite = location.state?.site_id || location.state?.siteId || '';
    const nextSite = String(fromStateSite || fromQuerySite || '').trim();
    if (nextSite) setSiteId(nextSite);

    const fromQueryDate = params.get('date') || '';
    const fromStateDate = location.state?.date || '';
    const nextDate = String(fromStateDate || fromQueryDate || '').trim();
    if (nextDate) setDate(nextDate);
  }, [location.search, location.state]);

  const refresh = async () => {
    if (!siteId) {
      setPeople([]);
      setSelectedId('');
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setError('');
    try {
      const [attendance, visitors, dailyWage] = await Promise.all([
        cameraAttendanceApi.getAttendanceOverstay({ site_id: siteId, date, signal: ac.signal }),
        cameraAttendanceApi.getVisitorOverstay({ site_id: siteId, date, signal: ac.signal }),
        cameraAttendanceApi.getDailyWageOverstay({ site_id: siteId, date, signal: ac.signal }),
      ]);
      const nextPeople = normalizeOverstayToPeople({ attendance, visitors, dailyWage });
      setPeople(nextPeople);
      setSelectedId(nextPeople[0]?.id || '');
    } catch (e) {
      setPeople([]);
      setSelectedId('');
      setError(e?.message || 'Failed to load overstay records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [siteId, date]);

  const filteredPeople = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return people;
    return people.filter(person => {
      const name = String(person.name || '').toLowerCase();
      const empId = String(person.employeeId || '').toLowerCase();
      return name.includes(query) || empId.includes(query);
    });
  }, [people, searchTerm]);

  useEffect(() => {
    if (!filteredPeople.length) return;
    const stillVisible = filteredPeople.some(person => person.id === selectedId);
    if (!stillVisible) setSelectedId(filteredPeople[0].id);
  }, [filteredPeople, selectedId]);

  const selectedPerson = useMemo(
    () =>
      filteredPeople.find(person => person.id === selectedId) ||
      filteredPeople[0] ||
      null,
    [filteredPeople, selectedId]
  );

  const handleConfirmForceCheckout = async reason => {
    if (!selectedPerson) return;
    try {
      if (selectedPerson.backendType === 'attendance') {
        await cameraAttendanceApi.forceCheckoutAttendance({ a_id: selectedPerson.recordId, reason });
      } else if (selectedPerson.backendType === 'visitor') {
        await cameraAttendanceApi.forceCheckoutVisitor({ entry_id: selectedPerson.recordId, reason });
      } else if (selectedPerson.backendType === 'daily_wage') {
        await cameraAttendanceApi.forceCheckoutDailyWage({ entry_id: selectedPerson.recordId, reason });
      }
      const updatedPeople = people.filter(person => person.id !== selectedPerson.id);
      setPeople(updatedPeople);
      setSelectedId(updatedPeople[0]?.id || '');
    } catch (e) {
      setError(e?.message || 'Force checkout failed.');
    }
  };

  return (
    <div className="w-full h-full grid grid-cols-1 lg:grid-cols-12 gap-3 relative">
      <aside className="lg:col-span-4 rounded-xl border border-neutral-200 bg-white/70 p-3 flex flex-col min-h-[520px]">
        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 flex items-center gap-2 mb-3">
          <ChevronLeft className="w-4 h-4 text-neutral-600" />
          <h2 className="text-sm font-semibold text-neutral-900">
            Employee/visitor over stay
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-2 mb-3">
          <div>
            <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
              Site ID
            </label>
            <input
              value={siteId}
              onChange={e => setSiteId(e.target.value)}
              placeholder="Enter site_id"
              className="w-full min-h-[44px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
            />
          </div>
          <div>
            <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
              Date (YYYY-MM-DD)
            </label>
            <input
              value={date}
              onChange={e => setDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-full min-h-[44px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
            />
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading || !siteId}
            className="min-h-[44px] rounded-2xl bg-green-600 text-white !text-white px-4 py-2 text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {error ? <p className="text-xs text-rose-600">{error}</p> : null}
        </div>

        <div className="mb-3">
          <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
            Search
          </label>
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by employee name or ID..."
            className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
          />
        </div>

        <div className="space-y-3 overflow-y-auto pr-1">
          {filteredPeople.map(person => {
            const isActive = person.id === selectedId;
            return (
              <button
                key={person.id}
                type="button"
                onClick={() => setSelectedId(person.id)}
                className={`w-full rounded-lg border px-3 py-2 flex items-center gap-3 text-left transition-colors ${
                  isActive
                    ? 'border-green-500 bg-green-50'
                    : 'border-neutral-200 bg-white hover:bg-neutral-50'
                }`}
              >
                <img
                  src={person.photo}
                  alt={person.name}
                  className="w-10 h-10 rounded-md object-cover"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">
                    {person.name}
                  </p>
                  <p className="text-sm text-neutral-600 truncate">
                    {person.employeeId} • {person.role}
                  </p>
                </div>
              </button>
            );
          })}
          {filteredPeople.length === 0 && (
            <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-600">
              {people.length ? 'No matching results.' : 'No overstay records available.'}
            </div>
          )}
        </div>
      </aside>

      <section className="lg:col-span-8 rounded-xl border border-neutral-200 bg-white/70 p-3 md:p-4 flex flex-col min-h-[520px]">
        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold tracking-wide text-neutral-900 uppercase">
            Face Recognition Status
          </h2>
          <button
            type="button"
            onClick={() =>
              openOverlay('forceCheckout', {
                title: 'Check Out',
                prompt: 'Do you want the Employee/visitor to force check out',
                personName: selectedPerson?.name || '',
                onConfirm: handleConfirmForceCheckout,
              })
            }
            disabled={!selectedPerson}
            className="rounded-lg bg-red-600 text-white !text-white px-4 py-2 text-sm font-medium hover:bg-red-700"
          >
            Force Check Out
          </button>
        </div>

        <div className="flex-1 rounded-lg border border-neutral-200 bg-white p-3 md:p-4 flex flex-col">
          {!selectedPerson ? (
            <div className="h-full flex items-center justify-center text-neutral-600">
              Select an employee or visitor to view details.
            </div>
          ) : (
            <>
          <div className="flex flex-col sm:flex-row gap-4">
            <img
              src={selectedPerson.photo}
              alt={selectedPerson.name}
              className="w-28 h-28 rounded-md object-cover border border-neutral-200"
            />

            <div className="space-y-1">
              <h3 className="text-2xl font-semibold text-neutral-900">Detected Person</h3>
              <p className="text-sm text-neutral-800">Name: {selectedPerson.name}</p>
              <p className="text-sm text-neutral-800">
                {selectedPerson.type === 'visitor' ? 'Visitor ID' : 'Employee ID'}:{' '}
                {selectedPerson.employeeId}
              </p>
              <p className="text-sm text-neutral-800">Confidence: {selectedPerson.confidence}</p>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-2xl font-semibold text-neutral-900">Shift: {selectedPerson.shift}</p>
            <p className="text-sm text-neutral-800 mt-2">In Time: {selectedPerson.inTime}</p>
            <p className="text-sm text-neutral-800">Out Time: {selectedPerson.outTime}</p>
          </div>

          <div className="mt-5">
            <h4 className="text-2xl font-bold text-neutral-900 uppercase mb-2">Safety Check</h4>
            <div className="space-y-2">
              {SAFETY_CHECK.map(item => (
                <div
                  key={item.label}
                  className="flex items-center justify-between rounded-lg bg-cyan-50/60 border border-cyan-100 px-3 py-2"
                >
                  <span className="text-sm text-neutral-800">{item.label}</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-md font-semibold ${
                      item.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                    }`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm font-semibold text-neutral-900">Message:</p>
            <p className="text-sm text-neutral-700">
              Please wear safety gloves before entering the production zone.
            </p>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-2xl font-bold text-neutral-900 uppercase">Today's Feed Log</h4>
              <button
                type="button"
                onClick={() =>
                  navigate('/camera-attendance/logs', {
                    state: { logs: TODAY_LOGS, fromOverstay: true },
                  })
                }
                className="text-sm font-semibold text-green-700 hover:text-green-800"
              >
                View All Logs
              </button>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 space-y-1">
              {TODAY_LOGS.slice(0, LOG_PREVIEW_COUNT).map(log => (
                <p key={`${selectedPerson.id}-${log}`} className="text-sm text-neutral-800">
                  {log}
                </p>
              ))}
              {TODAY_LOGS.length > LOG_PREVIEW_COUNT ? (
                <p className="text-xs text-neutral-500 pt-1">
                  Showing {LOG_PREVIEW_COUNT} of {TODAY_LOGS.length} logs.
                </p>
              ) : null}
            </div>
          </div>
            </>
          )}
        </div>
      </section>

    </div>
  );
};

export default CameraAttendanceOverstayScreen;
